import Spinner from "@/components/spinner";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useCreateResourceFileMutation } from "@/hooks/create-resource-file.hook";
import { useEditionById } from "@/hooks/edition-by-id.hook";
import { useResourceLibrary } from "@/hooks/resource-library.hook";
import { currentEditionIdAtom } from "@/store/edition-id.store";
import { zodResolver } from "@hookform/resolvers/zod";
import { Send2 } from "iconsax-react";
import { useAtomValue } from "jotai";
import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

/**
 * The data that is passed to the onOpenChange function when the drawer is closed
 * This contains the data related to the changed resource (document)
 */
export type OpenChangeData = null | {
  id: string;
};

type DocumentDrawerProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean, data: OpenChangeData) => void;
  addCurrentEditionTag?: boolean;
};

const RESOURCE_FILE_MAX_MB_SIZE = import.meta.env
  .VITE_RESOURCE_FILE_MAX_MB_SIZE;

const formSchema = z.object({
  file: z
    .custom<FileList>(
      (value) => value instanceof FileList,
      "Debes seleccionar un archivo PDF",
    )
    .refine(
      (files) => files && files.length === 1,
      "Selecciona un archivo PDF para subir",
    )
    .refine(
      (files) =>
        files &&
        files.length > 0 &&
        files[0].size <= RESOURCE_FILE_MAX_MB_SIZE * 1024 * 1024,
      (files) => ({
        message:
          files.length > 0
            ? `El archivo tiene un tamaño de ${(files[0].size / 1024 / 1024).toFixed(1)}MB. Por favor sube un archivo menor a ${RESOURCE_FILE_MAX_MB_SIZE}MB.`
            : "El archivo no es válido",
      }),
    )
    .refine(
      (files) => files && ["application/pdf"].includes(files[0]?.type || ""),
      `El formato es incorrecto. Debe ser un archivo PDF.`,
    ),
  name: z
    .string()
    .min(3, { message: "El nombre debe tener al menos 3 caracteres" })
    .max(255, { message: "El nombre no puede tener más de 255 caracteres" }),
});

/**
 * Shows a drawer for uploading a document to the resource library.
 * It allows the user to select a PDF file and provide a name for it.
 * The drawer can optionally add a tag for the current edition year.
 *
 * @param isOpen - Whether the drawer is open or not.
 * @param onOpenChange - Function to call when the drawer is closed or opened.
 * @param addCurrentEditionTag - Whether to add a tag for the current edition year to the document
 */
const DocumentDrawer: FC<DocumentDrawerProps> = ({
  isOpen,
  onOpenChange,
  addCurrentEditionTag,
}) => {
  const editionId = useAtomValue(currentEditionIdAtom);

  const { data: resourceLibrary } = useResourceLibrary();

  const { data: edition } = useEditionById({
    companyId: resourceLibrary?.owner.id || "",
    editionId,
  });

  const mutation = useCreateResourceFileMutation();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      file: undefined,
      name: "",
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    mutation.mutate({
      companyId: resourceLibrary?.owner.id || "",
      resourceLibraryId: resourceLibrary?.id || "",
      file: values.file[0],
      name: values.name.trim(),
      tags: addCurrentEditionTag ? [`editionyear::${edition?.year}`] : [],
    });
  };

  const handleOpenChange = (isOpen: boolean, data?: OpenChangeData) => {
    if (isOpen) {
      onOpenChange(isOpen, null);
    } else {
      form.reset();
      mutation.reset();
      onOpenChange(false, data || null);
    }
  };

  useEffect(() => {
    if (mutation.isSuccess) {
      toast(
        <span>
          El documento <strong>{mutation.variables.name}</strong> se ha subido
          exitosamente.
        </span>,
      );

      handleOpenChange(false, mutation.data);
    }
  }, [mutation]);

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent className="flex flex-col">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
            <SheetHeader>
              <SheetTitle>Nuevo documento</SheetTitle>
              <SheetDescription className="hidden">
                Publica un documento en la biblioteca de recursos
              </SheetDescription>
            </SheetHeader>

            <SheetBody>
              <div className="flex flex-col gap-4">
                <div className="flex flex-row gap-3 rounded-lg border border-[#615CB7] px-4 py-6">
                  <Send2 size={20} className="shrink-0 text-[#615CB7]" />

                  <div>
                    <h3 className="mb-2">Recuerda que...</h3>
                    <p className="text-sm">
                      El archivo debe pesar máximo {RESOURCE_FILE_MAX_MB_SIZE}MB
                      y estar en formato <strong>PDF</strong>.
                    </p>
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="file"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Archivo</FormLabel>
                      <FormControl>
                        <Input
                          type="file"
                          onChange={(e) => field.onChange(e.target.files)}
                          name={field.name}
                          ref={field.ref}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nombre</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Añade un nombre para el recurso"
                          minLength={3}
                          maxLength={255}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </SheetBody>

            <SheetFooter className="grid grid-cols-2 items-center">
              <Button
                type="submit"
                variant="default"
                size="lg"
                disabled={mutation.isPending}
              >
                {mutation.isPending && <Spinner className="mr-2" />}
                Subir recurso
              </Button>

              <Button
                type="button"
                variant="secondary"
                size="lg"
                onClick={() => handleOpenChange(false)}
              >
                Cancelar
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};

export default DocumentDrawer;
